import * as React from 'react'
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { AdmintoolService, OBSharedStyles } from '@pulseops/outbound'
import { AppContext, ErrorHandling, ModalComponent, Permission, RBAC, SelectOption, assets } from '@pulseops/common'
import { useIsFocused } from '@react-navigation/native'
import { pipe } from 'fp-ts/lib/function'
import { DrawerContentComponentProps, DrawerContentOptions } from '@react-navigation/drawer'
import { useForm } from 'react-hook-form'
import { IBGeneralField, IBGeneralTable, TypeInputComponent } from '@pulseops/inbound'
import { getStyleStatus, getTitleStatus, statusOption, validateMail } from '../outbound/commonFunction'
import { useLoading } from '@mxt/zio-react'
import { ZIO } from '@mxt/zio'

type StaffListForm = {
  user: string
  team: SelectOption | null
  extensionCode: string
  status: SelectOption | null
  id: string
}

type DataTable = {
  no: number
  id: string
  user: string
  team: string
  extensionCode: string
  status: string
}

interface Props extends DrawerContentComponentProps<DrawerContentOptions> { }
export const ConfigT4S18InboundStaffListScreen = ({ navigation }: Props) => {
  const { t } = useTranslation()
  const isFocused = useIsFocused()
  const [isOpenAddPopup, setOpenAddPopup] = React.useState<boolean>(false)
  const [isOpenEditPopup, setOpenEditPopup] = React.useState<boolean>(false)
  const roles: string[] = pipe(RBAC.permissions, ErrorHandling.runDidMount([]))
  const [dataTable, setDataTable] = React.useState<DataTable[]>([])
  const [isLoading, bindLoading] = useLoading(false)
  const [reload, setReload] = React.useState<boolean>(false)
  const { showGlobalLoading, showToast, changeBreadcrumb } = React.useContext(AppContext.AppContextInstance)
  const { height, width } = useWindowDimensions()
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [taskCount, setTaskCount] = React.useState<number>(0)
  const [teamOption, setTeamOptione] = React.useState<SelectOption[]>([])
  const [isFirstLoad, setFirstLoad] = React.useState<boolean>(true)

  const formStaffList = useForm<StaffListForm>({
    defaultValues: {
      user: '',
      team: null,
      extensionCode: '',
      status: statusOption[0]
    }
  })

  const filterColumnList = {
    user: '',
    team: '',
    extensionCode: ''
  }

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            navigation.navigate('HomeScreen')
          }
        },
        { title: t('menu:Settings'), navigate: () => navigation.navigate('SettingStack', { screen: 'SettingScreen' }) },
        { title: t('Setting:AdminTool'), navigate: () => navigation.navigate('SettingStack', { screen: 'SettingScreen', params: { tabTitle: 'adminTool' } }) },
        { title: t('Outbound:AdminTool:T4S18'), navigate: null }
      ])
    }

  }, [isFocused])

  React.useEffect(() => {
    showGlobalLoading(isLoading)
  }, [isLoading])

  React.useEffect(() => {
    pipe(
      ZIO.zip(
        AdmintoolService.getT4S18ConfigQuery({
          size: pageSize,
          start: pageNum,
          sort: 'ASC',
          orders: ['teamCode', 'staffEmail']
        }),
        AdmintoolService.getT4S19Config()
      ),
      ZIO.map(([res, team]) => {
        const listTeam = team.data?.map((i) => ({
          label: i.teamName ?? '',
          value: i.teamCode ?? ''
        })).sort((p1,p2) => p1.value > p2.value ? 1 : -1) || []
        res.data && setDataTable(res.data
        .map((i, index) => ({
          no: (pageNum * pageSize) + index + 1,
          id: i.id ?? '',
          user: i.staffEmail ?? '',
          team: listTeam && listTeam?.length > 0 ? (listTeam?.find((team) => i.teamCode && team.value === i.teamCode)?.label ?? '') : i.teamCode ?? '',
          extensionCode: i.extensionCode && i.extensionCode !== '-' ? i.extensionCode : '',
          status: i.status ? getTitleStatus(i.status) : ''
        })))
        setTaskCount(res.total)
        setTeamOptione(listTeam ?? [])
        setFirstLoad(false)
        return ZIO.unit
      }),
      bindLoading,
      ZIO.unsafeRun({})
    )
  }, [])

  React.useEffect(() => {
    if(!isFirstLoad) {
      pipe(
        AdmintoolService.getT4S18ConfigQuery({
          size: pageSize,
          start: pageNum,
          sort: 'ASC',
          orders: ['teamCode', 'staffEmail']
        }),
        ZIO.map((res) => {
          res.data && setDataTable(res.data
          .map((i, index) => ({
            no: (pageNum * pageSize) + index + 1,
            id: i.id ?? '',
            user: i.staffEmail ?? '',
            team: i.teamCode ? (teamOption?.find((team) => team.value === i.teamCode)?.label ?? '') : '',
            extensionCode: i.extensionCode ?? '',
            status: i.status ? getTitleStatus(i.status) : ''
          })))
          setTaskCount(res.total)
          reload && setReload(false)
          return ZIO.unit
        }),
        bindLoading,
        ZIO.unsafeRun({})
      )
    }
  }, [reload, pageSize, pageNum])

  const columns = [
    {
      label: t('Outbound:AdminTool:No'),
      field: 'no',
      disabled: true
    },
    {
      label: t('Outbound:AdminTool:User'),
      field: 'user'
    },
    {
      label: t('Outbound:AdminTool:Team'),
      field: 'team'
    },
    {
      label: t('Outbound:AdminTool:ExtensionCode'),
      field: 'extensionCode'
    },
    {
      label: t('Outbound:AdminTool:Status'),
      field: 'status',
      render: (value: string) => {
        return <Text style={getStyleStatus(value)}>{value}</Text>
      }
    },
    {
      label: '',
      field: 'id',
      render: (value: string) => {
        return (
          roles.includes(Permission.UpdateT4S18InboundStaffList) ?
            <TouchableOpacity
              onPress={() => {
                formStaffList.reset()
                setOpenEditPopup(true)
                const findData = dataTable.find((i) => i.id === value)
                formStaffList.setValue('id', findData?.id ?? '')
                formStaffList.setValue('user', findData?.user ?? '')
                formStaffList.setValue('extensionCode', findData?.extensionCode ?? '')
                formStaffList.setValue('status', statusOption.find((i) => i.label === findData?.status) ?? null)
                formStaffList.setValue('team', teamOption?.find((i) => i.label === findData?.team) ?? null)
              }}
            >
              <assets.OBPencilGray />
            </TouchableOpacity> :
            <></>
        )
      }
    }
  ]

  const validateExistMail = (mail: string, idValue: string) => {
    return pipe(
      AdmintoolService.getT4S18ConfigQuery({
        size: 10,
        start: 0,
        staffEmail: mail
      }),
      ZIO.map((res) => {
        const findData = res.data && res.data.find((i) => i.staffEmail && i.staffEmail.toLocaleLowerCase() === mail.toLocaleLowerCase() && i.id !== idValue)
        if(findData) {
          formStaffList.setError('user', { message: t('message:IB0020') })
          return false 
        }
        else {
          formStaffList.clearErrors('user')
          return true
        }
      }),
      bindLoading,
      ZIO.unsafeRun({})
    )
  }

  const validateExistExtensionCode = (code: string, idValue: string) => {
    const findData = dataTable.find((i) => i.extensionCode === code && i.id !== idValue && i.status === 'Active')
    if (findData) return false
    return true
  }

  const AddButton = () => {
    return (
      <View style={{ justifyContent: 'flex-end', flexDirection: 'row', paddingBottom: 10 }}>
        <TouchableOpacity
          onPress={() => {
            formStaffList.reset()
            setOpenAddPopup(true)
          }}
        >
          <View style={[OBSharedStyles.actionContent, OBSharedStyles.actionHightLight]}>
            <Text style={OBSharedStyles.actionHightLightText}>
              {t('Outbound:AdminTool:AddButton')}
            </Text>
          </View>
        </TouchableOpacity>
      </View>
    )
  }

  const TableConfig = () => {
    return (
      <View style={{ flexDirection: 'row' }}>
        <IBGeneralTable
          dataTable={columns}
          data={dataTable}
          maxWidthContainer
          maxHeight={height - height * 0.3}
          maxWidth={width - width * 0.025}
          filterColumn={filterColumnList}
          paging={{
            page: pageNum,
            pageSize: pageSize,
            taskCount: taskCount,
            setPage: setPageNum,
            setPageSize: setPageSize
          }}
        >
        </IBGeneralTable>
      </View>
    )
  }

  const FieldEdit = () => {
    return (
      <View style={{ paddingHorizontal: 15, paddingVertical: 10 }}>
        <IBGeneralField
          FieldForm={formStaffList}
          col={2}
          typeInput={[
            {
              type: TypeInputComponent.INPUT,
              formName: 'user',
              title: t('Outbound:AdminTool:User'),
              inputType: 'input',
              required: true,
              rules: {
                required: { value: true, message: t('message:OB0019', { field: t('Outbound:AdminTool:User') }) },
                validate: (value: string) => {
                  if (!validateMail(value))
                    return t('message:OB0028')
                  // if (value && !validateExistMail(value, formStaffList.watch('id'))) {
                  //   return t('message:IB0020')
                  // }
                  return true
                }
              },
              maxLength: 100
            },
            {
              type: TypeInputComponent.SELECT,
              formName: 'team',
              title: t('Outbound:AdminTool:Team'),
              option: teamOption ?? [],
              required: true,
              rules: {
                required: { value: true, message: t('message:OB0019', { field: t('Outbound:AdminTool:Team') }) },
              }
            },
            {
              type: TypeInputComponent.INPUT,
              formName: 'extensionCode',
              title: t('Outbound:AdminTool:ExtensionCode'),
              inputType: 'number',
              required: true,
              rules: {
                required: { value: true, message: t('message:OB0019', { field: t('Outbound:AdminTool:ExtensionCode') }) },
                validate: (value: string) => {
                  if (value && !validateExistExtensionCode(value, formStaffList.watch('id'))) {
                    return t('message:OB0096')
                  }
                  return true
                }
              },
              maxLength: 5
            },
            {
              type: TypeInputComponent.SELECT,
              formName: 'status',
              title: t('Outbound:AdminTool:Status'),
              option: statusOption,
              required: true,
              rules: {
                required: { value: true, message: t('message:OB0019', { field: t('Outbound:AdminTool:Status') }) },
              }
            }
          ]}
        >
        </IBGeneralField>
      </View>
    )
  }

  const AddPopup = () => {
    return (
      <ModalComponent
        children={FieldEdit()}
        visible={isOpenAddPopup}
        onClose={() => setOpenAddPopup(false)}
        title={t('Outbound:AdminTool:CreateNew')}
        actions={[
          {
            text: t('common:Cancel'),
            type: 'outline',
            action: () => setOpenAddPopup(false),
            disabled: isLoading,
            loading: false,
            onlyWide: false,
            style: { height: 35, marginRight: 10 }
          },
          {
            text: t('common:Add'),
            type: 'filled',
            action: () => onAdd(),
            disabled: isLoading,
            loading: false,
            onlyWide: false,
            style: { height: 35 }
          }
        ]}
        centerTitle
        modalWidth={800}
      />
    )
  }

  const EditPopup = () => {
    return (
      <ModalComponent
        children={FieldEdit()}
        visible={isOpenEditPopup}
        onClose={() => setOpenEditPopup(false)}
        title={t('Outbound:AdminTool:Edit')}
        actions={[
          {
            text: t('common:Cancel'),
            type: 'outline',
            action: () => setOpenEditPopup(false),
            disabled: isLoading,
            loading: false,
            onlyWide: false,
            style: { height: 35, marginRight: 10 }
          },
          {
            text: t('common:Save'),
            type: 'filled',
            action: () => onSave(),
            disabled: isLoading,
            loading: false,
            onlyWide: false,
            style: { height: 35 }
          }
        ]}
        centerTitle
        modalWidth={800}
      />
    )
  }

  const onSave = formStaffList.handleSubmit(async (value) => {
    const validate = await validateExistMail(value.user, value.id)
    if(validate) {
      pipe(
        AdmintoolService.updateT4S18Config({
          id: value.id,
          staffEmail: value.user,
          extensionCode: value.extensionCode,
          status: value.status?.value ?? '',
          teamCode: value.team?.value ?? ''
        }),
        ZIO.map((res) => {
          setReload(true)
          setOpenEditPopup(false)
          return ZIO.unit
        }),
        ZIO.catchAll((error) => {
          showToast(error.source.message, 'error')
          setOpenEditPopup(false)
          return ZIO.unit
        }),
        bindLoading,
        ZIO.unsafeRun({})
      )
    }
  })

  const onAdd = formStaffList.handleSubmit(async (value) => {
    const validate = await validateExistMail(value.user, value.id)
    if(validate) {
      pipe(
        AdmintoolService.addT4S18Config({
          staffEmail: value.user,
          extensionCode: value.extensionCode,
          status: value.status?.value ?? '',
          teamCode: value.team?.value ?? ''
        }),
        ZIO.map((res) => {
          setReload(true)
          setOpenAddPopup(false)
          return ZIO.unit
        }),
        ZIO.catchAll((error) => {
          showToast(error.source.message, 'error')
          setOpenAddPopup(false)
          return ZIO.unit
        }),
        bindLoading,
        ZIO.unsafeRun({})
      )
    }
  })

  return (
    <View style={[styles.container]}>
      {
        roles.includes(Permission.ViewT4S18InboundStaffList) &&
        <>
          {roles.includes(Permission.CreateNewT4S18InboundStaffList) && AddButton()}
          {TableConfig()}
          {AddPopup()}
          {EditPopup()}
        </>
      }
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    backgroundColor: '#FFFFFF',
  },
  header: {
    color: '#000000',
    fontSize: 24,
    fontWeight: 'bold'
  },
  addButton: {
    color: '#FFFFFF',
    backgroundColor: '#ED1B2E',
    flexDirection: 'row',
    borderRadius: 8,
  }
})
